interface Props {
  wpm: number;
  threshold?: number;
}

export function WPMIndicator({ wpm, threshold = 170 }: Props) {
  const getStatus = () => {
    if (wpm === 0) return { icon: '—', msg: '', color: 'text-gray-400', bgColor: 'bg-gray-100' };
    if (wpm < 110) return { icon: '🐢', msg: 'Slow', color: 'text-blue-600', bgColor: 'bg-blue-50' };
    if (wpm <= 150) return { icon: '✓', msg: 'Good', color: 'text-green-600', bgColor: 'bg-green-50' };
    if (wpm <= threshold) return { icon: '⚡', msg: 'Fast', color: 'text-yellow-600', bgColor: 'bg-yellow-50' };
    return { icon: '⚠️', msg: 'Slow down!', color: 'text-red-600', bgColor: 'bg-red-50' };
  };

  const status = getStatus();

  return (
    <div className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-md ${status.bgColor}`}>
      <span className={`font-mono text-sm font-medium ${status.color}`}>
        {wpm || '—'}
      </span>
      <span className="text-xs text-gray-500">WPM</span>
      {wpm > 0 && (
        <span className={`text-sm ${status.color}`} title={status.msg}>
          {status.icon}
        </span>
      )}
    </div>
  );
}

// WPM calculation helper
interface WPMSegment {
  words: number;
  durationMs: number;
  timestamp: number;
}

const WPM_WINDOW_MS = 30000; // 30 second rolling window

export function calculateWPM(recentSegments: WPMSegment[]): number {
  const now = Date.now();
  const cutoff = now - WPM_WINDOW_MS;

  // Filter to only recent segments
  const recent = recentSegments.filter(s => s.timestamp > cutoff);

  if (recent.length === 0) return 0;

  const totalWords = recent.reduce((sum, s) => sum + s.words, 0);
  const totalMs = recent.reduce((sum, s) => sum + s.durationMs, 0);

  if (totalMs === 0) return 0;

  return Math.round((totalWords / totalMs) * 60000); // Words per minute
}

export function addWPMSegment(
  segments: WPMSegment[],
  text: string,
  durationMs: number,
  speaker: number
): WPMSegment[] {
  // Only track user's speech (speaker 0)
  if (speaker !== 0) return segments;

  const words = text.split(/\s+/).filter(w => w.length > 0).length;

  const now = Date.now();
  const cutoff = now - WPM_WINDOW_MS;

  // Add new segment and filter old ones
  return [
    ...segments.filter(s => s.timestamp > cutoff),
    { words, durationMs, timestamp: now }
  ];
}
